import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { ConditionRow } from './ConditionRow';
import { daysPastEta, getEscalation, sortConditions } from '../../utils/conditions';
import type { ConditionPatch } from '../../hooks/useConditions';
import type { Condition, SortState } from '../../types/conditions';
interface ConditionSectionProps {
  title: string;
  conditions: Condition[];
  emptyMessage: string;
  sort: SortState | null;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onUpdate: (id: string, patch: ConditionPatch) => void;
  onComments: (id: string) => void;
  onUpload: (id: string, fileName: string) => void;
  onLinkDocuments: (id: string) => void;
  defaultOpen?: boolean;
  /** Show which group (Processor / Admin / Title) each row belongs to. */
  showCategory?: boolean;
  tone?: SectionTone;
}

/** Graduated depth within the single brand-blue hue: 1 = lightest, 4 = deepest. */
export type SectionTone = 1 | 2 | 3 | 4;

/**
 * One hue (brand blue #1F64AD), stepped in lightness. Count badges stay dark enough
 * for white text to meet WCAG AA at every step.
 */
const toneStyles: Record<SectionTone, {
  header: string;
  icon: string;
  count: string;
}> = {
  1: {
    header: 'border-l-[#8DB2DA] bg-brand/[0.05] hover:bg-brand/[0.09]',
    icon: 'text-brand',
    count: 'bg-[#3A76B9]'
  },
  2: {
    header: 'border-l-[#5C8FC7] bg-brand/[0.09] hover:bg-brand/[0.13]',
    icon: 'text-brand',
    count: 'bg-brand'
  },
  3: {
    header: 'border-l-brand bg-brand/[0.13] hover:bg-brand/[0.17]',
    icon: 'text-brand-dark',
    count: 'bg-[#1A5797]'
  },
  4: {
    header: 'border-l-brand-dark bg-brand/[0.17] hover:bg-brand/[0.21]',
    icon: 'text-brand-dark',
    count: 'bg-brand-dark'
  }
};
export function ConditionSection({
  title,
  conditions,
  emptyMessage,
  sort,
  selectedIds,
  onToggleSelect,
  onUpdate,
  onComments,
  onUpload,
  onLinkDocuments,
  defaultOpen = true,
  showCategory = false,
  tone = 1
}: ConditionSectionProps) {
  const t = toneStyles[tone];
  const [open, setOpen] = useState(defaultOpen);
  const sorted = sortConditions(conditions, sort);
  const needsAttention = conditions.filter(c => daysPastEta(c) > 0 || getEscalation(c) !== 'none').length;
  const panelId = `group-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return <section aria-label={`${title} conditions`} data-id="element-147" data-mp="2z7gibc08mkyh">
      <h3 data-id="element-148" data-mp="2z8rm3btonspl">
        <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-controls={panelId} className={`flex w-full items-center gap-3 border-y border-l-4 border-y-slate-200 py-3 pl-5 pr-6 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${t.header}`} data-id="element-149" data-mp="338a707li83sf">
          <motion.span animate={{
          rotate: open ? 0 : -90
        }} transition={{
          duration: 0.15
        }} className="flex" data-id="element-150" data-mp="1isf9o37umkwo">
            <ChevronDownIcon className={`h-4 w-4 ${t.icon}`} aria-hidden="true" data-id="element-151" data-mp="1b8nw8r6fv3p5" />
          </motion.span>
          <span className="text-[15px] font-semibold text-charcoal" data-id="element-152" data-mp="16ih62mwa3b4s">{title}</span>
          <span className={`min-w-[22px] rounded-full px-1.5 py-0.5 text-center text-xs font-semibold tabular-nums text-white ${t.count}`} data-id="element-153" data-mp="11j4c7i6zp3sp">
            {conditions.length}
          </span>
          {needsAttention > 0 && <span className="inline-flex items-center gap-1.5 rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600" data-id="element-154" data-mp="1u6gzjukz5fn2">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden="true" data-id="element-155" data-mp="1p6m6bkdq8dyu" />
              {needsAttention} need{needsAttention === 1 ? 's' : ''} attention
            </span>}
        </button>
      </h3>

      <AnimatePresence initial={false} data-id="element-156" data-mp="8uffrwgvpmna">
        {open && <motion.div id={panelId} role="rowgroup" initial={{
        height: 0,
        opacity: 0,
        overflow: 'hidden'
      }} animate={{
        height: 'auto',
        opacity: 1,
        transitionEnd: {
          overflow: 'visible'
        }
      }} exit={{
        height: 0,
        opacity: 0,
        overflow: 'hidden'
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} data-id="element-157" data-mp="8vqlw2xsr6hk">
            {conditions.length === 0 ? <p className="border-b border-slate-100 px-6 py-4 pl-[60px] text-sm text-slate-500" data-id="element-158" data-mp="1ipj22f9hj1op">{emptyMessage}</p> : <AnimatePresence initial={false} data-id="element-159" data-mp="2bfhy4um3jz2v">
                {sorted.map(c => <ConditionRow key={c.id} condition={c} selected={selectedIds.has(c.id)} onToggleSelect={() => onToggleSelect(c.id)} onUpdate={patch => onUpdate(c.id, patch)} onComments={() => onComments(c.id)} onUpload={fileName => onUpload(c.id, fileName)} onLinkDocuments={() => onLinkDocuments(c.id)} showCategory={showCategory} data-id="element-160" data-mp="2geugb220whz4" />)}
              </AnimatePresence>}
          </motion.div>}
      </AnimatePresence>
    </section>;
}